/**
 * Web token storage — the TokenProvider implementation the HttpClient (T3)
 * consumes.
 *
 * This module is the ONLY file in the codebase (outside tests) that touches
 * `localStorage`. Mobile will implement its own TokenProvider backed by
 * expo-secure-store or native keystore. CP1 forbids `localStorage` in shared.
 *
 * Legacy parity:
 *   - Key names match `frontend/src/services/apiClient.ts` + `Auth.tsx`:
 *       * `sessionToken` — short-lived access JWT (Bearer token)
 *       * `user`         — cached User object JSON (re-hydrated on bootstrap)
 *     Preserving the key names keeps any dev who has an existing legacy
 *     session logged in when they point their browser at the new shell.
 *   - `userLocation` is legacy flotsam (geocoding cache) — not our concern.
 *
 * Security caveat (flagged, NOT fixed in T7):
 *   Storing tokens in localStorage is XSS-exposed. Industry guidance is
 *   httpOnly cookie for refresh + in-memory access + CSRF on refresh. The
 *   refresh token ALREADY lives in an httpOnly cookie set by the backend
 *   (good), but the access token is here in localStorage (anti-pattern).
 *   Moving the access token to an in-memory map is a post-migration `sec:`
 *   followup — see the T7 handoff notes. Do NOT add more secrets to
 *   localStorage in the meantime.
 */

import type { TokenProvider } from '@shopista/shared/api';

/** Legacy localStorage key for the JWT access token. */
export const ACCESS_TOKEN_KEY = 'sessionToken';
/** Legacy localStorage key for the cached User JSON. */
export const CACHED_USER_KEY = 'user';

/**
 * Extended TokenProvider surface used by the web shell. Adds a couple of
 * convenience methods that `TokenProvider` itself doesn't need (the cached
 * user, and `setTokens` that writes both fields atomically). The HttpClient
 * only uses the base `TokenProvider` subset; extras are consumed by the OAuth
 * callback + session bootstrap.
 */
export interface WebTokenStorage extends TokenProvider {
  /**
   * Write access token + cached user. Called by the OAuth callback once the
   * backend responds with a freshly-minted session.
   */
  setSession(input: { accessToken: string; user: unknown }): void;

  /** Read the cached user blob (may be stale — always validate via /auth/me). */
  getCachedUser(): unknown | null;

  /** Clear everything this adapter wrote. Called on logout + refresh failure. */
  clearTokens(): void;
}

/**
 * Ambient typing for `localStorage` — apps/web's tsconfig pulls in `lib.dom`
 * so the global is available without an explicit declaration. Tests inject a
 * fake via `storage` argument below.
 */
interface LocalStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface MakeTokenStorageOptions {
  /**
   * Override the backing store — primarily a test hook. Defaults to
   * `window.localStorage`.
   */
  storage?: LocalStorageLike;
}

function safeGet(storage: LocalStorageLike, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    // Quota / disabled — treat as "no token".
    return null;
  }
}

function safeSet(storage: LocalStorageLike, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Quota / disabled — best effort.
  }
}

function safeRemove(storage: LocalStorageLike, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Build a web-side TokenProvider. `storage` defaults to `window.localStorage`
 * when the DOM is available; tests can inject a fake implementation.
 */
export function makeTokenStorage(
  options: MakeTokenStorageOptions = {}
): WebTokenStorage {
  const storage =
    options.storage ??
    (typeof window !== 'undefined' ? window.localStorage : undefined);

  if (!storage) {
    throw new Error(
      'makeTokenStorage(): no localStorage available. Pass `options.storage` explicitly (e.g. from jsdom or a fake).'
    );
  }

  return {
    getAccessToken(): string | null {
      return safeGet(storage, ACCESS_TOKEN_KEY);
    },

    setAccessToken(token: string | null): void {
      if (token === null) {
        safeRemove(storage, ACCESS_TOKEN_KEY);
      } else {
        safeSet(storage, ACCESS_TOKEN_KEY, token);
      }
    },

    clearTokens(): void {
      safeRemove(storage, ACCESS_TOKEN_KEY);
      safeRemove(storage, CACHED_USER_KEY);
    },

    setSession({ accessToken, user }): void {
      safeSet(storage, ACCESS_TOKEN_KEY, accessToken);
      try {
        safeSet(storage, CACHED_USER_KEY, JSON.stringify(user));
      } catch {
        // user wasn't serializable — store nothing, access token alone is
        // sufficient for bootstrap (/auth/me will re-fetch canonical user).
      }
    },

    getCachedUser(): unknown | null {
      const raw = safeGet(storage, CACHED_USER_KEY);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        // Corrupt blob — clear it and return null so bootstrap falls through
        // to /auth/me.
        safeRemove(storage, CACHED_USER_KEY);
        return null;
      }
    },
  };
}
